/*
================================================================================
FILE HEADER — apps/studio-web/src/components/RunConfigSheet.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Option A — Run Config    | Created — browser    |
|    |            |                   | Sheet (Section 25)       | selector modal shown |
|    |            |                   |                          | before Run Selected  |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- RunConfig         — run-time settings for one execution (browser, delay, timeout, etc.)
- DEFAULT_RUN_CONFIG— sensible defaults used if user never opens the sheet
- RunConfigSheet    — modal component: browser, step settings, CLI preview

IMPORTANT POINTS
----------------
1. Two execution targets:
     "studio"  → extension bridge, always Chrome. Browser selector is disabled.
     "cli"     → shows generated `plex run` command for copy-paste into CI.
   The Studio Execution tab always runs via the extension bridge regardless of
   which target is displayed — "cli" mode is informational only (shows command).

2. Browser detection cannot be done from the browser context (no fs access).
   All three browsers are shown when CLI mode is selected; a note directs the
   user to `plex browsers` or `npx playwright install <browser>`.

3. stepDelayMs: time to wait BETWEEN steps. 0 = as fast as possible.
4. stepTimeoutMs: how long a single step may wait for a result before timing out.
5. stopOnFirstFailure: when false, all steps run even after a failure.
   Useful for seeing the full failure scope before fixing.

6. Parallel: reserved for Phase 4. Shown as disabled with a tooltip.
================================================================================
*/

import { useState } from 'react';
import type { DBTest } from '../lib/db.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type BrowserChoice = 'chromium' | 'firefox' | 'webkit';
export type ExecutionTarget = 'studio' | 'cli';

export interface RunConfig {
  target:             ExecutionTarget;
  browser:            BrowserChoice;
  headless:           boolean;
  stepDelayMs:        number;
  stepTimeoutMs:      number;
  stopOnFirstFailure: boolean;
  parallel:           1 | 2 | 4;
}

export const DEFAULT_RUN_CONFIG: RunConfig = {
  target:             'studio',
  browser:            'chromium',
  headless:           false,
  stepDelayMs:        300,
  stepTimeoutMs:      6000,
  stopOnFirstFailure: true,
  parallel:           1,
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  tests:    DBTest[];
  initial?: RunConfig;
  onRun:    (config: RunConfig) => void;
  onCancel: () => void;
};

const STEP_DELAY_OPTIONS: Array<{ label: string; value: number }> = [
  { label: '0 ms',   value: 0    },
  { label: '100 ms', value: 100  },
  { label: '300 ms', value: 300  },
  { label: '500 ms', value: 500  },
  { label: '1 s',    value: 1000 },
];

const TIMEOUT_OPTIONS: Array<{ label: string; value: number }> = [
  { label: '3 s',  value: 3000  },
  { label: '6 s',  value: 6000  },
  { label: '10 s', value: 10000 },
  { label: '20 s', value: 20000 },
  { label: '30 s', value: 30000 },
];

const BROWSER_OPTIONS: Array<{ id: BrowserChoice; label: string; icon: string; desc: string }> = [
  { id: 'chromium', label: 'Chromium', icon: '🔵', desc: 'Chrome / Edge engine' },
  { id: 'firefox',  label: 'Firefox',  icon: '🦊', desc: 'Mozilla Firefox'      },
  { id: 'webkit',   label: 'WebKit',   icon: '🧭', desc: 'Safari engine'        },
];

export function RunConfigSheet({ tests, initial = DEFAULT_RUN_CONFIG, onRun, onCancel }: Props) {
  const [config, setConfig] = useState<RunConfig>({ ...initial });

  const totalSteps = tests.reduce((n, t) => n + (t.stepCount ?? 0), 0);
  const testCount  = tests.length;

  const set = <K extends keyof RunConfig>(key: K, val: RunConfig[K]) =>
    setConfig(prev => ({ ...prev, [key]: val }));

  // Build the CLI command string for copy-paste
  const cliCommand = buildCliCommand(tests, config);

  const handleRun = () => onRun(config);

  return (
    /* ── Backdrop ── */
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* ── Sheet ── */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#111113',
          border: '1px solid #3f3f46',
          borderRadius: 12,
          width: 480,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center',
          padding: '18px 20px 14px',
          borderBottom: '1px solid #27272a',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>Configure Run</div>
            <div style={{ fontSize: 12, color: '#71717a', marginTop: 2 }}>
              {testCount} test{testCount !== 1 ? 's' : ''} · {totalSteps} step{totalSteps !== 1 ? 's' : ''} total
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}
            onMouseEnter={e => { (e.currentTarget).style.color = '#a1a1aa'; }}
            onMouseLeave={e => { (e.currentTarget).style.color = '#52525b'; }}
          >✕</button>
        </div>

        <div style={{ padding: '20px' }}>

          {/* ── Execution Mode ── */}
          <Section label="EXECUTION MODE">
            <div style={{ display: 'flex', gap: 8 }}>
              <ModeCard
                active={config.target === 'studio'}
                onClick={() => set('target', 'studio')}
                icon="🌐"
                title="Studio"
                desc="Attached Chrome tab via extension"
                badge="● Live"
                badgeColor="#22c55e"
              />
              <ModeCard
                active={config.target === 'cli'}
                onClick={() => set('target', 'cli')}
                icon="⌨️"
                title="CLI / CI"
                desc="Playwright — any browser, headless"
                badge="plex run"
                badgeColor="#7c3aed"
              />
            </div>
          </Section>

          {/* ── Browser selector (CLI only) ── */}
          <Section label="BROWSER" dimmed={config.target === 'studio'}>
            {config.target === 'studio' ? (
              <div style={{ fontSize: 12, color: '#52525b', padding: '10px 12px', background: '#0d0d10', borderRadius: 6, border: '1px solid #27272a' }}>
                Studio execution always uses the browser tab attached via the Chrome extension.
                Switch to <strong style={{ color: '#a1a1aa' }}>CLI / CI</strong> mode to select Firefox or WebKit.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {BROWSER_OPTIONS.map(b => (
                    <BrowserPill
                      key={b.id}
                      browser={b}
                      selected={config.browser === b.id}
                      onClick={() => set('browser', b.id)}
                    />
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.headless}
                    onChange={e => set('headless', e.target.checked)}
                    style={{ accentColor: '#7c3aed', width: 14, height: 14 }}
                  />
                  <span style={{ fontSize: 12, color: '#a1a1aa' }}>
                    Headless mode <span style={{ color: '#52525b' }}>(no browser window — faster, for CI)</span>
                  </span>
                </label>
                <div style={{ marginTop: 10, fontSize: 11, color: '#52525b', padding: '6px 10px', background: '#0d0d10', borderRadius: 5, border: '1px solid #1c1c1f' }}>
                  Need a browser? Run: <code style={{ color: '#a78bfa' }}>npx playwright install {config.browser}</code>
                  &nbsp;·&nbsp;
                  Or check: <code style={{ color: '#a78bfa' }}>plex browsers</code>
                </div>
              </>
            )}
          </Section>

          {/* ── Step Settings ── */}
          <Section label="STEP SETTINGS">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Step delay */}
              <div>
                <div style={{ fontSize: 11, color: '#71717a', marginBottom: 6 }}>Delay between steps</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {STEP_DELAY_OPTIONS.map(opt => (
                    <PillButton
                      key={opt.value}
                      active={config.stepDelayMs === opt.value}
                      onClick={() => set('stepDelayMs', opt.value)}
                      label={opt.label}
                    />
                  ))}
                </div>
              </div>

              {/* Step timeout */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 11, color: '#71717a', minWidth: 100 }}>Timeout per step</div>
                <select
                  value={config.stepTimeoutMs}
                  onChange={e => set('stepTimeoutMs', Number(e.target.value))}
                  style={{
                    background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6,
                    padding: '5px 10px', color: '#e4e4e7', fontSize: 12, outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {TIMEOUT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <span style={{ fontSize: 11, color: '#52525b' }}>per step before timeout</span>
              </div>

              {/* Stop on first failure */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.stopOnFirstFailure}
                  onChange={e => set('stopOnFirstFailure', e.target.checked)}
                  style={{ accentColor: '#7c3aed', width: 14, height: 14 }}
                />
                <span style={{ fontSize: 12, color: '#a1a1aa' }}>
                  Stop test on first failure
                  <span style={{ color: '#52525b' }}> (uncheck to run all steps for full failure picture)</span>
                </span>
              </label>
            </div>
          </Section>

          {/* ── Parallelism ── */}
          <Section label="PARALLELISM">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <select
                disabled
                value={1}
                style={{
                  background: '#0d0d10', border: '1px solid #27272a', borderRadius: 6,
                  padding: '5px 10px', color: '#52525b', fontSize: 12, cursor: 'not-allowed',
                }}
              >
                <option value={1}>1 — Sequential</option>
              </select>
              <span style={{ fontSize: 11, color: '#3f3f46' }}>Parallel execution unlocks in Phase 4 (cloud)</span>
            </div>
          </Section>

          {/* ── CLI command preview (CLI mode only) ── */}
          {config.target === 'cli' && (
            <Section label="GENERATED CLI COMMAND">
              <div style={{ background: '#0d0d10', border: '1px solid #27272a', borderRadius: 6, padding: '10px 12px' }}>
                <code style={{ fontSize: 11, color: '#a78bfa', display: 'block', wordBreak: 'break-all', lineHeight: 1.6 }}>
                  {cliCommand}
                </code>
              </div>
              <button
                onClick={() => void navigator.clipboard.writeText(cliCommand)}
                style={{
                  marginTop: 8, background: 'none', border: '1px solid #3f3f46',
                  borderRadius: 5, padding: '4px 12px', color: '#71717a',
                  cursor: 'pointer', fontSize: 11,
                }}
                onMouseEnter={e => { (e.currentTarget).style.color = '#a1a1aa'; }}
                onMouseLeave={e => { (e.currentTarget).style.color = '#71717a'; }}
              >
                Copy Command
              </button>
            </Section>
          )}

          {/* ── Footer buttons ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, paddingTop: 16, borderTop: '1px solid #27272a' }}>
            <button
              onClick={onCancel}
              style={{
                background: 'none', border: '1px solid #3f3f46', borderRadius: 7,
                padding: '8px 18px', color: '#a1a1aa', cursor: 'pointer', fontSize: 13,
              }}
              onMouseEnter={e => { (e.currentTarget).style.borderColor = '#52525b'; }}
              onMouseLeave={e => { (e.currentTarget).style.borderColor = '#3f3f46'; }}
            >
              Cancel
            </button>
            <button
              onClick={handleRun}
              style={{
                background: '#7c3aed', border: 'none', borderRadius: 7,
                padding: '8px 22px', color: '#fff', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                boxShadow: '0 0 0 1px rgba(124,58,237,0.5)',
              }}
              onMouseEnter={e => { (e.currentTarget).style.background = '#6d28d9'; }}
              onMouseLeave={e => { (e.currentTarget).style.background = '#7c3aed'; }}
            >
              ▶ Run {testCount} Test{testCount !== 1 ? 's' : ''}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function Section({ label, children, dimmed = false }: {
  label: string;
  children: React.ReactNode;
  dimmed?: boolean;
}) {
  return (
    <div style={{ marginBottom: 20, opacity: dimmed ? 0.5 : 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#52525b', letterSpacing: '0.1em', marginBottom: 8 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ModeCard({ active, onClick, icon, title, desc, badge, badgeColor }: {
  active: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  desc: string;
  badge: string;
  badgeColor: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, textAlign: 'left', padding: '12px 14px',
        background: active ? 'rgba(124,58,237,0.12)' : '#18181b',
        border: `1.5px solid ${active ? '#7c3aed' : '#27272a'}`,
        borderRadius: 8, cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: active ? '#c4b5fd' : '#e4e4e7' }}>{title}</span>
        {active && (
          <span style={{
            marginLeft: 'auto', fontSize: 9, fontWeight: 700,
            color: badgeColor, border: `1px solid ${badgeColor}`,
            borderRadius: 20, padding: '1px 6px',
          }}>{badge}</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#71717a' }}>{desc}</div>
    </button>
  );
}

function BrowserPill({ browser, selected, onClick }: {
  browser: { id: BrowserChoice; label: string; icon: string; desc: string };
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={browser.desc}
      style={{
        flex: 1, padding: '8px 10px', textAlign: 'center',
        background: selected ? 'rgba(124,58,237,0.15)' : '#18181b',
        border: `1.5px solid ${selected ? '#7c3aed' : '#27272a'}`,
        borderRadius: 7, cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      <div style={{ fontSize: 16, marginBottom: 3 }}>{browser.icon}</div>
      <div style={{ fontSize: 11, fontWeight: selected ? 700 : 400, color: selected ? '#c4b5fd' : '#a1a1aa' }}>
        {browser.label}
      </div>
    </button>
  );
}

function PillButton({ active, onClick, label }: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        background: active ? '#7c3aed' : '#18181b',
        border: `1px solid ${active ? '#7c3aed' : '#3f3f46'}`,
        borderRadius: 20, cursor: 'pointer',
        fontSize: 11, fontWeight: active ? 700 : 400,
        color: active ? '#fff' : '#71717a',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CLI command builder
// ─────────────────────────────────────────────────────────────────────────────

function buildCliCommand(tests: DBTest[], config: RunConfig): string {
  const parts = ['plex run'];

  // If all tests are in one suite we could glob — for now list concept
  if (tests.length === 1) {
    parts.push('<test-file>.json');
  } else {
    parts.push(`<${tests.length}-test-files>.json`);
  }

  if (config.browser !== 'chromium') {
    parts.push(`--browser ${config.browser}`);
  }
  if (config.headless) {
    parts.push('--headless');
  }
  if (config.stepDelayMs !== 0) {
    parts.push(`--delay ${config.stepDelayMs}`);
  }
  if (config.stepTimeoutMs !== 6000) {
    parts.push(`--timeout ${config.stepTimeoutMs}`);
  }
  parts.push('--reporter all');

  return parts.join(' ');
}
